import { Label } from "@EditArticleContainer/shared/components/EditableTextContainer/types/editableText.types";
import {
  FinancialTermsLASCField,
  MainFinancialTermsTableItem,
  MilestonePaymentItem,
  OtherPaymentItem,
  RoyaltyItem,
} from "@EditArticleContainer/shared/types/paragraphContents/contents/financialTermsLASCContent.types";

const toLabels = (labels: unknown): Label[] => (labels ?? []) as Label[];

/** primary 값이 비어있으면 fallback 필드를 사용한다. (둘 다 비면 fallback 그대로 → 빈 값) */
const resolveField = (
  primary: FinancialTermsLASCField,
  fallback: FinancialTermsLASCField,
): FinancialTermsLASCField =>
  (primary.value.at(0)?.text ?? "").trim() !== "" ? primary : fallback;

export const payingAxisValues = (item: MainFinancialTermsTableItem): string[] =>
  item["Paying Partner Axis(LA&SC)"].value
    .map((v) => v.text ?? "")
    .filter(Boolean);

export const receivingAxisValues = (
  item: MainFinancialTermsTableItem,
): string[] =>
  item["Receiving Partner Axis(LA&SC)"].value
    .map((v) => v.text ?? "")
    .filter(Boolean);

export const upfrontText = (item: MainFinancialTermsTableItem) =>
  item["Upfront Payment(LA&SC)"].value.at(0)?.text ?? "";

export const upfrontLabels = (item: MainFinancialTermsTableItem): Label[] =>
  toLabels(item["Upfront Payment(LA&SC)"].value.at(0)?.labels);

export const milestoneValueText = (milestone: MilestonePaymentItem) =>
  milestone["Milestone Payment(LA&SC)"].value.at(0)?.text ?? "";

export const milestoneValueLabels = (
  milestone: MilestonePaymentItem,
): Label[] =>
  toLabels(milestone["Milestone Payment(LA&SC)"].value.at(0)?.labels);

export const milestoneDetailText = (milestone: MilestonePaymentItem) =>
  milestone["Milestone Payment Detail(LA&SC)"].value.at(0)?.text ?? "";

export const milestoneDetailLabels = (
  milestone: MilestonePaymentItem,
): Label[] =>
  toLabels(milestone["Milestone Payment Detail(LA&SC)"].value.at(0)?.labels);

export const otherValueText = (other: OtherPaymentItem) =>
  other["Other Payment(LA&SC)"].value.at(0)?.text ?? "";

export const otherValueLabels = (other: OtherPaymentItem): Label[] =>
  toLabels(other["Other Payment(LA&SC)"].value.at(0)?.labels);

export const otherDetailText = (other: OtherPaymentItem) =>
  other["Other Payment Detail(LA&SC)"].value.at(0)?.text ?? "";

export const otherDetailLabels = (other: OtherPaymentItem): Label[] =>
  toLabels(other["Other Payment Detail(LA&SC)"].value.at(0)?.labels);

export const royaltyRateText = (royalty: RoyaltyItem) =>
  royalty["Royalty Rate"].value.at(0)?.text ?? "";

export const royaltyRateLabels = (royalty: RoyaltyItem): Label[] =>
  toLabels(royalty["Royalty Rate"].value.at(0)?.labels);

export const royaltyDetailText = (royalty: RoyaltyItem) =>
  royalty["Royalty Detail"].value.at(0)?.text ?? "";

export const royaltyDetailLabels = (royalty: RoyaltyItem): Label[] =>
  toLabels(royalty["Royalty Detail"].value.at(0)?.labels);

const resolveTotalField = (item: MainFinancialTermsTableItem) =>
  resolveField(
    item["Total Payment(LA&SC)"],
    item["Disclosed Payment Sum(LA&SC)"],
  );

export const totalText = (item: MainFinancialTermsTableItem) =>
  resolveTotalField(item).value.at(0)?.text ?? "";

export const totalLabels = (item: MainFinancialTermsTableItem): Label[] =>
  toLabels(resolveTotalField(item).value.at(0)?.labels);

/** 메인 테이블 항목에 표출할 값(axis 포함)이 하나라도 있는지 여부 */
export const hasMainTableItemValue = (
  item: MainFinancialTermsTableItem,
): boolean => {
  const texts = [
    upfrontText(item),
    totalText(item),
    ...item["Milestone Payment And Detail"].flatMap((milestone) => [
      milestoneValueText(milestone),
      milestoneDetailText(milestone),
    ]),
    ...item["Other Payment And Detail"].flatMap((other) => [
      otherValueText(other),
      otherDetailText(other),
    ]),
    ...item["Royalty(LA&SC)"].flatMap((royalty) => [
      royaltyRateText(royalty),
      royaltyDetailText(royalty),
    ]),
  ];

  return (
    texts.some((text) => text.trim() !== "") ||
    payingAxisValues(item).length > 0 ||
    receivingAxisValues(item).length > 0
  );
};

export const otherFinancialTermsText = (field: FinancialTermsLASCField) =>
  field.value.at(0)?.text ?? "";

export const otherFinancialTermsLabels = (
  field: FinancialTermsLASCField,
): Label[] => toLabels(field.value.at(0)?.labels);
